import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, type Persona } from "../../../data";
import { WebLayout } from "../../../components/shell/WebLayout";
import { GoogleAnmeldung } from "../../../components/GoogleAnmeldung";
import { rememberOAuthConsent } from "../../../lib/zustimmung-vormerkung";
import { herkunftAus, ruecksprungMitHerkunft } from "../../../lib/herkunft";
import { authFehler, authFehlerArt } from "../../konto/auth";
import { CaptchaFeld, captchaConfig } from "../../konto/Captcha";
import { rememberAgencyName, rememberKontoArt, rememberTrialPlan, rememberZielgruppe, trialPlanFromQuery } from "./Einrichtung";
import { tarifName } from "../../../model/tarif";
import { rememberCode, rememberKauf } from "./ZahlungsmethodeStart";
import { B2bBestaetigung, RechtstexteZustimmung, rememberPendingConsent, settlePendingConsents } from "./Lebenszyklus";
import { Check, b2bMissingText, creatorNurUnternehmerText, legalDoc } from "./model";
import { WEBSITE_URL } from "./parts";
import { loadCurrentLegalVersions } from "./data";
import { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH, passwordHint } from "@talentkontor/domain";
import { t, currentLang, mitSprache } from "../../../lib/sprache";
import { fehlerText } from "../../../lib/fehler";
import { KontoVorhanden, PostfachPruefen } from "../../konto/PostfachPruefen";

/**
 * Konto anlegen (10_website/registrierung.html). Danach geht es in der Einrichtung weiter: erst Schritt 1 mit der Wahl der
 * Darstellung (0121, Dunkel vorausgewählt), dann die Firmendaten. Dorthin führt auch der Link aus der Bestätigungsmail.
 * Die Zustimmung zu AGB, Datenschutzhinweisen und AVV wird mit Fassung und Zeitpunkt gespeichert (user_consents): der Tag
 * des Ankreuzens wird gemerkt und nach der Anmeldung mit der dann geltenden Fassung eingetragen (ohne Funktion für Anonyme).
 * Dazu eine eigene, nicht vorangekreuzte Pflicht-Checkbox „als Unternehmer“ (Anwaltsvorgabe 06.10.2026), gespeichert als
 * Zustimmung „b2b_declaration“ mit Weg Registrierung bzw. Google (0182).
 */
export function Registrieren() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Tarif aus der Preisseite (?tarif=team): die Testphase läuft dann in diesem Tarif (Befund 18)
  // Selbständige Creator (0178): ?art=creator oder ?tarif=creator; eigenes Creator-Konto im Tarif Creator statt Agentur
  const creator = params.get("art") === "creator" || params.get("tarif") === "creator";
  const tarifKey = creator ? null : trialPlanFromQuery(params.get("tarif"));
  const tarif = tarifKey ? { key: tarifKey, name: tarifName(tarifKey) } : creator ? { key: "creator", name: "Creator" } : null;
  // „Sofort starten“ auf der Preisseite (?kaufen=1, 0169): im Zahlungsschritt ist der Abschluss ohne Testphase vorausgewählt
  const kaufen = params.get("kaufen") === "1";
  useEffect(() => {
    if (tarifKey) rememberTrialPlan(tarifKey);
    rememberKontoArt(creator ? "creator" : null);
    rememberZielgruppe(params.get("zielgruppe"));
    rememberKauf(kaufen);
    rememberCode(params.get("code"));
  }, [tarifKey, kaufen, creator]);
  // 0219: Firma aus der persönlichen Startseite (?firma=…) vorausfüllen; nur Text, begrenzt
  const [name] = useState(() => (creator ? "" : (params.get("firma") ?? "").replace(/[<>]/g, "").trim().slice(0, 120)));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [b2b, setB2b] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Demo-Vorschau der Postfach-Seite (?vorschau=postfach): in der Demo gibt es keine Bestätigungsmail
  const [confirm, setConfirm] = useState<string | null>(api.mode === "demo" && params.get("vorschau") === "postfach" ? "lena.sommer@example.com" : null);
  const [vorhanden, setVorhanden] = useState<string | null>(api.mode === "demo" && params.get("vorschau") === "vorhanden" ? "lena.sommer@example.com" : null);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const session = api.session();
  // CAPTCHA (Befund 9): Supabase verlangt das Token bei der Registrierung, sobald CAPTCHA eingeschaltet ist
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaRunde, setCaptchaRunde] = useState(0);
  const mitCaptcha = api.mode !== "demo" && !!captchaConfig();

  // 0412 (Befund 5a): die heute geltenden Fassungen merken, damit nach der Anmeldung genau ihnen zugestimmt wird
  const [fassungen, setFassungen] = useState<Record<string, string> | null>(null);
  useEffect(() => {
    let alive = true;
    loadCurrentLegalVersions(api).then((v) => alive && setFassungen(v as Record<string, string>)).catch(() => undefined);
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    let alive = true;
    api.personas?.().then((p) => alive && setPersonas(p)).catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);
  const chef = personas.find((p) => p.detail.startsWith("Admin"));

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // Bauliste Vertrieb 13.10.2026 (Punkt 2): ein Knopf „Konto anlegen“; Testen oder Sofortkauf wählt man erst im Abschluss-Schritt.
    // Wer von der Preisseite mit „Sofort starten“ kommt (?kaufen=1), findet dort den Sofortkauf vorgewählt.
    const sofort = kaufen;
    const c = new Check();
    // Punkt 3: Der Name wird hier nicht mehr gefragt (ein Feld weniger); kommt er mit ?firma=, gilt er still, sonst fragt ihn der
    // Schritt nach dem Code als einziges Feld
    const agency = name.trim().length >= 2 ? name.trim().slice(0, 120) : null;
    const mail = c.email("email", email, { label: creator ? t("deine E-Mail", "your email") : t("deine geschäftliche E-Mail", "your business email"), required: true });
    if (api.mode !== "demo") c.text("password", password, { label: t("ein Passwort", "a password"), required: true, min: MIN_PASSWORD_LENGTH, max: MAX_PASSWORD_LENGTH });
    if (!accepted) c.fail("accepted", t("Bitte die Bedingungen akzeptieren.", "Please accept the terms."));
    if (!b2b) c.fail("b2b", b2bMissingText());
    if (mitCaptcha && !captcha) c.fail("captcha", t("Bitte die Sicherheitsprüfung lösen.", "Please complete the security check."));
    setErrors(c.errors);
    if (!c.ok || !mail) return;
    setBusy(true);
    setError(null);
    try {
      if (agency) rememberAgencyName(agency);
      rememberKauf(sofort);
      // Vormerkung hängt an dieser Mailadresse (G-WEB-4)
      const geltend = fassungen ?? (await loadCurrentLegalVersions(api).catch(() => null)) as Record<string, string> | null;
      rememberPendingConsent(mail, ["agency_terms", "privacy", "dpa", "b2b_declaration"], geltend);
      // Art, Name, Tarif und Weg auch im Konto vormerken: Der Browser-Speicher allein ging verloren, wenn die Seite später ohne
      // ?art=creator aufgerufen wurde oder der Bestätigungslink in einem anderen Browser aufging (Befund 06.10.2026)
      const vormerkung = { art: creator ? ("creator" as const) : null, name: agency ?? null, plan: tarifKey, kaufen: sofort, segment: params.get("zielgruppe"),
        ...herkunftAus(params) };
      const r = await api.signUp(mail, password, { captchaToken: captcha, redirectTo: "/einrichtung/registrierung", language: currentLang(), vormerkung });
      if (r.needsConfirmation) {
        setConfirm(mail);
      } else {
        // AGB und Datenschutz sofort; den AVV fragt die Datenbank erst ab dem Admin (nach dem Anlegen der Agentur)
        await settlePendingConsents().catch(() => undefined);
        // Inhaber 03.10.2026: Schnellstart (Agentur anlegen, dann Zahlung); Darstellung und Firmendaten danach
        navigate("/einrichtung/registrierung");
      }
    } catch (err) {
      if (authFehlerArt(err) === "user_exists") setVorhanden(mail);
      else setError(authFehler(err));
      setCaptchaRunde((n) => n + 1);
    } finally {
      setBusy(false);
    }
  }
  async function demo() {
    if (!chef) return;
    setBusy(true);
    try {
      await api.signInAs?.(chef.userId);
      navigate("/agentur", { replace: true });
    } catch (err) {
      setError(fehlerText(err));
    } finally {
      setBusy(false);
    }
  }

  const err = (k: string) => errors[k] && <small id={`reg-${k}`} className="tk-error">{errors[k]}</small>;
  return (
    <WebLayout title={t("Konto anlegen", "Create account")}>
      <main id="main" className="web">
        {/* Nach dem Absenden springt die ganze Seite um: Postfach prüfen (Inhaber 06.10.2026) */}
        {vorhanden ? <div className="centerform"><KontoVorhanden email={vorhanden} onBack={() => { setVorhanden(null); setPassword(""); }} /></div> : confirm ? <div className="centerform"><PostfachPruefen email={confirm} redirectTo="/einrichtung/registrierung" onBack={() => { setConfirm(null); setPassword(""); }}
          onVerified={() => navigate("/einrichtung/registrierung", { replace: true })} /></div> : (
        <form className="centerform panel tk-auth" onSubmit={(e) => void submit(e)} noValidate aria-busy={busy}>
          <div className="tk-auth-intro">
          <h1>{creator ? t("Dein Creator-Business. Gut organisiert.", "Your creator business. Well organised.") : t("Deine Agentur. Gut organisiert.", "Your agency. Well organised.")}</h1>
          {/* Punkt 1: drei Zeilen statt eines Absatzes; die Kosten nach dem Test stehen mit Datum dort, wo abgeschlossen wird */}
          <ul className="tk-reg-punkte">
            <li>{t("14 Tage kostenlos testen, im vollen Umfang.", "Try it free for 14 days, with the full feature set.")}</li>
            <li>{t("Heute 0,00 €. Vor Testende jederzeit beendbar, dann wird nichts abgebucht.", "€0.00 today. Can be ended any time before the trial ends; then nothing is charged.")}</li>
            <li>{creator ? t("Marken, Verträge und Rechnungen an einem Ort.", "Brands, contracts and invoices in one place.") : t("Deine Creator bekommen ihr eigenes Portal.", "Your creators get a portal of their own.")}</li>
          </ul>
          {tarif && !creator && <p className="tk-reg-art">{t(`Vorgewählt: Tarif ${tarif.name}. Ändern kannst du ihn im nächsten Schritt.`, `Preselected: the ${tarif.name} plan. You can change it in the next step.`)}</p>}
          {creator && <p>{t("Creator-Tarife: Creator ab 19 € netto im Monat, Creator Pro mit Anfragen, Media-Kits und Export für 29 € netto im Monat, ", "Creator plans: Creator from €19 net per month, Creator Pro with inquiries, media kits and export for €29 net per month, ")}{creatorNurUnternehmerText()}</p>}
          {/* Wer über einen allgemeinen „Kostenlos testen“-Knopf kommt, findet hier den richtigen Kontotyp (Befund Gesamtprüfung 07.10.2026) */}
          <p className="tk-reg-art">{creator
            ? <>{t("Du führst eine Agentur oder ein Management? ", "Running an agency or management? ")}<Link to="/registrieren" replace>{t("Zur Agentur-Registrierung", "Sign up as an agency")}</Link></>
            : <>{t("Du bist selbständiger Creator ohne Agentur? ", "Independent creator without an agency? ")}<Link to="/registrieren?art=creator" replace>{t("Eigenes Creator-Konto (ab 19 € netto im Monat)", "Your own creator account (from €19 net per month)")}</Link></>}</p>
          </div>
          <div className="tk-auth-felder stack">
          {!session && (
            <GoogleAnmeldung redirectPath={ruecksprungMitHerkunft("/einrichtung/registrierung", herkunftAus(params))} vorher={() => {
              if (!accepted) return t("Bitte zuerst unten AGB, Auftragsverarbeitungsvertrag und Datenschutzhinweise ankreuzen.", "Please first tick the terms, data processing agreement and privacy notice below.");
              if (!b2b) return t("Bitte zuerst unten bestätigen, dass du als Unternehmer handelst.", "Please first confirm below that you are acting as a business.");
              if (name.trim().length >= 2) rememberAgencyName(name.trim());
              // Zustimmung und B2B-Bestätigung kurz vormerken; nach der Rückkehr von Google trägt die Einrichtung sie mit Fassung ein
              rememberOAuthConsent(["agency_terms", "privacy", "dpa", "b2b_declaration"], Date.now(), fassungen);
              return null;
            }} fenster={{
              // Inhaber 13.10.2026: Wer zuerst auf Google klickt, hakt die Bedingungen in einem Fenster an statt eine Fehlermeldung zu lesen
              offen: !accepted || !b2b, bereit: accepted && b2b, titel: t("Kurz bestätigen", "Quick confirmation"),
              inhalt: <>
                <p>{t("Bitte bestätige noch die Bedingungen für dein Konto. Danach geht es mit Google direkt weiter.", "Please confirm the terms for your account. After that you continue with Google right away.")}</p>
                <RechtstexteZustimmung id="reg-google-agb" checked={accepted} onChange={setAccepted} />
                <B2bBestaetigung id="reg-google-b2b" checked={b2b} onChange={setB2b} />
              </>,
            }} />
          )}
          {session && <p className="notice">{t("Du bist angemeldet als ", "You are signed in as ")}{session.email}. <Link to="/einrichtung/registrierung">{t("Weiter", "Continue")}</Link></p>}
          <label>
            {creator ? t("E-Mail", "Email") : t("Geschäftliche E-Mail", "Business email")}<input id="geschftlicheemail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email"
              aria-invalid={errors.email ? true : undefined} aria-describedby={errors.email ? "reg-email" : undefined} />
            {err("email")}
          </label>
          <label>
            {t("Passwort", "Password")}<input id="passwort" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password"
              required={api.mode !== "demo"} minLength={api.mode !== "demo" ? MIN_PASSWORD_LENGTH : undefined}
              aria-invalid={errors.password ? true : undefined} aria-describedby={errors.password ? "reg-password" : "reg-password-hinweis"} />
            {errors.password ? err("password") : api.mode !== "demo" && <small id="reg-password-hinweis">{passwordHint(currentLang())}</small>}
          </label>
          <label className="row tk-check">
            <input type="checkbox" required checked={accepted} onChange={(e) => setAccepted(e.target.checked)}
              aria-invalid={errors.accepted ? true : undefined} aria-describedby={errors.accepted ? "reg-accepted" : undefined} />
            <span>
              {t("Ich akzeptiere die ", "I accept the ")}<a href={`${WEBSITE_URL}${legalDoc("agency_terms")!.path}`} target="_blank" rel="noreferrer">{t("AGB", "Terms")}</a> {t("und den ", "and the ")}<a href={`${WEBSITE_URL}${legalDoc("dpa")!.path}`} target="_blank" rel="noreferrer">{t("Auftragsverarbeitungsvertrag", "data processing agreement")}</a> {t("und habe die ", "and have read the ")}<a href={`${WEBSITE_URL}${legalDoc("privacy")!.path}`} target="_blank" rel="noreferrer">{t("Datenschutzhinweise", "Privacy notice")}</a> {t("gelesen (jeweils in der heute geltenden Fassung).", "(each in the version valid today).")}</span>
          </label>
          {err("accepted")}
          <B2bBestaetigung id="reg-b2b-feld" checked={b2b} onChange={setB2b} error={errors.b2b} />
          {mitCaptcha && <CaptchaFeld onToken={setCaptcha} resetKey={captchaRunde} />}
          {err("captcha")}
          {error && <p className="tk-error" role="alert">{error}</p>}
          <button className="primary tk-reg-knopf" type="submit" disabled={busy}>{busy ? t("Wird angelegt …", "Creating …") : t("Konto anlegen", "Create account")}</button>
          {api.mode === "demo" && chef && <button className="btn " type="button" onClick={() => void demo()} disabled={busy}>{t("Demo öffnen", "Open demo")}</button>}
          {api.mode === "demo" && <small>{t("Demo im Browser: erfundene Daten, nichts wird übertragen. Das neue Konto gilt bis zum Neuladen.", "Demo in the browser: fictional data, nothing is transmitted. The new account lasts until you reload.")}</small>}
          <small>{t("Schon ein Konto? ", "Already have an account? ")}<Link to={mitSprache("/anmelden", currentLang())}>{t("Anmelden", "Sign in")}</Link></small>
          </div>
        </form>
        )}
      </main>
    </WebLayout>
  );
}
